import React, { useEffect, useState } from 'react';
import { MsalProvider as MsalAuthProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../utils/msalConfig';

// Create and initialize MSAL instance immediately
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL synchronously
// This is important: we need to initialize it before any component tries to use it
let msalInitialized = false;
msalInstance.initialize()
  .then(() => {
    console.log('MSAL initialized successfully at startup');
    msalInitialized = true;
  })
  .catch(error => {
    console.error('Error initializing MSAL at startup:', error);
  });

// Export for use in hooks or components
export const useMsalInstance = () => {
  return msalInstance;
};

// MSAL Provider wrapper component that handles initialization status
const MsalProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(msalInitialized);
  const [error, setError] = useState(null);

  // Check MSAL initialization status and wait if needed
  useEffect(() => {
    if (msalInitialized) {
      setIsInitialized(true);
      return;
    }

    // If MSAL isn't initialized yet, wait for it
    const checkInitialization = async () => {
      try {
        if (!msalInstance.initialized) {
          await msalInstance.initialize();
          msalInitialized = true;
          console.log('MSAL initialized in MsalProvider effect');
        }
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize MSAL in provider:', error);
        setError(error);
      }
    };

    checkInitialization();

    // Also set up a timeout to check initialization status
    const interval = setInterval(() => {
      if (msalInstance.initialized) {
        msalInitialized = true;
        setIsInitialized(true);
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Show a loading state until MSAL is initialized
  if (!isInitialized && !error) {
    // You could return a loading indicator here if needed
    return null;
  }

  // Show error state if initialization failed
  if (error) {
    console.error('MSAL initialization failed:', error);
    return (
      <div className="text-red-600 p-4">
        Authentication service initialization failed. Please reload the page.
      </div>
    );
  }

  // Once initialized, render the provider with children
  return (
    <MsalAuthProvider instance={msalInstance}>
      {children}
    </MsalAuthProvider>
  );
};

// Custom hook to use MSAL authentication status and methods
export const useMsalAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [isInitialized, setIsInitialized] = useState(msalInitialized);

  // Check only initialization status on mount without triggering login
  useEffect(() => {
    // Just check if MSAL is already initialized
    if (msalInstance.initialized || msalInitialized) {
      setIsInitialized(true);
    }

    // Set up a safety check interval to detect when initialization completes
    const interval = setInterval(() => {
      if (msalInstance.initialized || msalInitialized) {
        setIsInitialized(true);
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval);
  }, []);

  // Check if user is authenticated
  const isAuthenticated = accounts.length > 0;

  // Get active account safely
  const getActiveAccount = () => {
    // Don't try to get active account if not initialized
    if (!isInitialized || !msalInstance.initialized) {
      return null;
    }

    try {
      return instance.getActiveAccount() || (accounts.length > 0 ? accounts[0] : null);
    } catch (error) {
      console.error('Error getting active account:', error);
      return null;
    }
  };

  // Get active account only if initialized
  const activeAccount = isInitialized ? getActiveAccount() : null;

  // Force new login - logs out first, then logs in again
  const forceNewLogin = async () => {
    try {
      console.log('Force new login requested - logging out first');

      // First, ensure we're initialized
      if (!msalInstance.initialized) {
        console.log('Initializing MSAL before forced login...');
        await msalInstance.initialize();
        msalInitialized = true;
        setIsInitialized(true);
      }

      // Now proceed with a fresh login with prompt: 'select_account' to force account selection
      // We can't combine prompt values as it causes AADSTS90023 error
      console.log('Starting login with account selection prompt');
      const result = await instance.loginPopup({
        prompt: 'select_account', // Force account selection dialog
        scopes: ["User.Read",
          "Files.Read",
          "Files.Read.All",
          "Files.ReadWrite",
          "Files.ReadWrite.All",
          "Sites.Read.All"]
      });
      console.log('Login successful:', result);
      
      // If we successfully logged in, check if we have the token
      if (result && result.accessToken) {
        // Store the token for easier access later
        sessionStorage.setItem('oneDriveToken', result.accessToken);
      }
      
      return true;
    } catch (error) {
      // Handle user cancelling the login
      if (error.name === 'PopupCancelledError' || error.name === 'BrowserAuthError') {
        console.log('Login popup was cancelled by user');
        clearAuthState();
        return false;
      }
      console.error("Force login error:", error);
      return false;
    }
  };

  // Login function - only called when explicitly requested
  const login = async () => {
    try {
      console.log('Explicit login requested');
      // Ensure MSAL is initialized before login
      if (!msalInstance.initialized) {
        console.log('Initializing MSAL before login...');
        await msalInstance.initialize();
        msalInitialized = true; // Update the global flag
        setIsInitialized(true);
      }

      // Wait a small amount of time to ensure initialization is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Trigger the login popup
      console.log('Showing login popup...');
      const result = await instance.loginPopup();
      console.log('Login successful:', result);
      return true;
    } catch (error) {
      // Handle user cancelling the login
      if (error.name === 'PopupCancelledError' || error.name === 'BrowserAuthError') {
        console.log('Login popup was cancelled by user');

        // Clear any partial authentication state
        try {
          // Try to reset any partial auth state
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            // If we have any partial auth state, clear it
            console.log('Clearing partial authentication state after popup closed');
            sessionStorage.removeItem('msal.interaction.status');
            localStorage.removeItem('msal.interaction.status');
          }
        } catch (clearError) {
          console.error('Error clearing auth state:', clearError);
        }

        return false;
      }
      console.error("Login error:", error);
      return false;
    }
  };

  // Clear all auth state (used when popup is closed without logging in)
  const clearAuthState = () => {
    try {
      console.log('Clearing all MSAL authentication state');
      // Clear MSAL cache
      sessionStorage.removeItem('msal.interaction.status');
      localStorage.removeItem('msal.interaction.status');

      // Clear OneDrive token
      sessionStorage.removeItem('oneDriveToken');

      // Clear any other auth-related state
      const msalKeys = Object.keys(localStorage).filter(key => key.startsWith('msal.'));
      msalKeys.forEach(key => localStorage.removeItem(key));

      const sessionMsalKeys = Object.keys(sessionStorage).filter(key => key.startsWith('msal.'));
      sessionMsalKeys.forEach(key => sessionStorage.removeItem(key));

      return true;
    } catch (error) {
      console.error('Error clearing auth state:', error);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Only try to logout if we're initialized and have an active account
      if (!msalInstance.initialized) {
        console.log('Cannot logout - MSAL not initialized');
        clearAuthState(); // Still clear auth state even if MSAL isn't initialized
        return true;
      }

      // Check if we have any accounts before trying to logout
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) {
        console.log('No accounts to logout');
        clearAuthState(); // Clear auth state anyway
        return true;
      }

      // Try regular logout
      try {
        await instance.logoutPopup();
      } catch (logoutError) {
        console.error('Error during logout popup:', logoutError);
        // If the popup fails, just clear the state manually
        clearAuthState();
      }

      return true;
    } catch (error) {
      console.error("Logout error:", error);
      // Try to clear state manually even if logout fails
      clearAuthState();
      return false;
    }
  };

  return {
    instance,
    accounts,
    activeAccount,
    isAuthenticated,
    isAuthenticating: inProgress !== "none",
    isInitialized,
    login,
    logout,
    clearAuthState,
    forceNewLogin // Add the forceNewLogin method
  };
};

export default MsalProvider;